import { devices_settings } from 'src/stores/device-runtime/status';
import { format } from 'svelte-i18n';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import {
	CameraCloudStorageSubscriptionStatus,
	type CameraCloudStorageActivePlan,
	type CameraCloudUpsellParams,
} from 'src/types/CameraCloudStorage';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { user_data, user_settings } from 'src/stores/account/user';
import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
import { devices } from 'src/stores/lists/devices';
import { real_id } from 'src/helpers/interface_helpers';
import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
import {
	getSlotCountsFromSettings,
	isCameraAssignableToCloudSlot,
	isCameraCloudSlotPoolMaxed,
	resolveCameraCloudUpsellAction,
} from 'src/helpers/camera_cloud_storage_helpers';
import { cameraSlotIdEquals } from 'src/helpers/camera_cloud_storage_slot_ids';
import {
	getStorageError,
	type CameraCloudSlotAction,
	type CameraCloudSlotState,
	type NfsConfig,
	type StorageSeverity,
	type StorageStatus,
	type StorageUsage,
} from '../types/camera-storage';
import { STORAGE_BUSY_ERRORS, STORAGE_STATE_LABELS } from './camera-errors';
import { isCameraEncryptionConfigured, isCameraEncryptionSupported } from './camera-features';
import { getCameraDevices } from './camera-helpers';

const STORAGE_METHOD = {
	SET_CONFIG: 'Storage.SetConfig',
	FORMAT: 'Storage.Format',
	EJECT: 'Storage.Eject',
} as const;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
const BYTES_PER_UNIT = 1024;
const PERCENT_MAX = 100;
const LOW_FREE_PERCENT = 20;
const CRITICAL_FREE_PERCENT = 10;

export const isStorageBusy = (status: StorageStatus | undefined): boolean =>
	status?.errors?.some((error) => {
		const normalized = getStorageError(error);
		return normalized ? STORAGE_BUSY_ERRORS.includes(normalized) : false;
	}) ?? false;

export const getStorageComponentKey = (device?: Device | null): string => {
	return `storage:0`;
};

export const getStorageStatus = (device: Device): StorageStatus | undefined => {
	const id = real_id(device.id);
	return readDeviceStatus(id)?.[getStorageComponentKey(device)];
};

export const getStorageNfsConfig = (device: Device): NfsConfig | undefined => {
	const id = real_id(device.id);
	return readDeviceSettings(id)?.[getStorageComponentKey(device)]?.nfs;
};

export const formatBytes = (bytes: number): string => {
	if (!bytes || bytes <= 0) return `0 ${BYTE_UNITS[0]}`;

	let value = bytes;
	let unitIndex = 0;
	while (value >= BYTES_PER_UNIT && unitIndex < BYTE_UNITS.length - 1) {
		value /= BYTES_PER_UNIT;
		unitIndex += 1;
	}

	return `${value.toFixed(2)} ${BYTE_UNITS[unitIndex]}`;
};

export const getStorageUsage = (status: StorageStatus | undefined): StorageUsage => {
	const total = status?.fs_size || 0;
	const free = status?.fs_free || 0;
	const used = Math.max(total - free, 0);
	const freePercent = total > 0 ? Math.round((free / total) * PERCENT_MAX) : 0;
	const usedPercent = Math.min(PERCENT_MAX, Math.max(0, PERCENT_MAX - freePercent));

	return {
		used_label: formatBytes(used),
		total_label: formatBytes(total),
		free_percent: freePercent,
		used_percent: usedPercent,
	};
};

/** Remaining free space as an indicator colour: green, yellow under 20%, red under 10%. */
export const getStorageSeverity = (freePercent: number): StorageSeverity => {
	if (freePercent <= CRITICAL_FREE_PERCENT) return 'danger';
	if (freePercent <= LOW_FREE_PERCENT) return 'warning';
	return 'success';
};

export const getStorageStateLabel = (
	status: StorageStatus | undefined,
): { key: string; default: string } | null => {
	const activeError = status?.errors
		?.map(getStorageError)
		.find((error) => error && STORAGE_STATE_LABELS[error]);
	if (activeError) return STORAGE_STATE_LABELS[activeError];

	if (!status?.present) {
		return {
			key: 'camera_storage_state_no_card',
			default: 'No SD card present',
		};
	}

	if (status.active) {
		return { key: 'camera_storage_state_ready', default: 'Ready' };
	}

	return null;
};

export const setStorageNfsConfig = async (
	device: Device,
	nfs: NfsConfig | null,
	successMessage: string,
): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;
	const storageKey = getStorageComponentKey(device);

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.SET_CONFIG, {
		id: channel,
		config: { nfs },
	});
	onSettingsResponse(res, device, {
		success: successMessage,
		danger: _format('camera_storage_save_error', {
			default: 'Could not apply storage settings. Please retry.',
		}),
	});

	if (!res?.isok) return false;

	const id = real_id(device.id);
	const settings = readDeviceSettings(id);
	devices_settings.update_devices_settings(id, {
		...settings,
		[storageKey]: { ...settings?.[storageKey], nfs },
	});
	return true;
};

export const formatStorage = async (device: Device): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.FORMAT, { id: channel });
	onSettingsResponse(res, device, {
		success: _format('camera_storage_format_started_toast', {
			default: 'Formatting started.',
		}),
		danger: _format('camera_storage_format_error', {
			default: 'Could not format the storage. Please retry.',
		}),
	});
	return !!res?.isok;
};

export const ejectStorage = async (device: Device): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.EJECT, { id: channel });
	onSettingsResponse(res, device, {
		success: _format('camera_storage_eject_success_toast', {
			default: 'Storage ejected.',
		}),
		danger: _format('camera_storage_eject_error', {
			default: 'Could not eject the storage. Please retry.',
		}),
	});
	return !!res?.isok;
};

/** What this camera has to clear first, then the account-level upsell. */
const resolveCameraCloudSlotAction = (
	params: CameraCloudUpsellParams & {
		needsSetup: boolean;
		isAssigned: boolean;
		isAssignable: boolean;
		hasFreeSlots: boolean;
	},
): CameraCloudSlotAction => {
	if (params.needsSetup) return 'setup';
	if (params.isAssigned) return 'none';
	// Buying or freeing a slot would still leave out a camera that can never take one.
	if (!params.isAssignable) return 'none';
	if (params.hasFreeSlots) return 'activate';
	if (isCameraCloudSlotPoolMaxed(params)) return 'manage';
	return resolveCameraCloudUpsellAction(params);
};

/**
 * Cloud slot state for one camera, as opposed to the local SD card the rest of
 * this file covers. A camera that can never take a slot (shared, or not a
 * camera) reports `none`; home-level permission stays with the caller, which
 * sees the same state but may not act on it (`canManageCameraCloudStorageSlots`).
 *
 * @param params.deviceStatus - Looked up by real id when omitted; Svelte callers
 * pass `$devices_status[realId]` to stay reactive.
 * @param params.hasKeyBackup - Local or cloud recording-key backup. The cloud
 * lookup is async, so it stays with the caller.
 */
export const getCameraCloudSlotState = ({
	device,
	deviceStatus,
	hasKeyBackup,
	slotAssignments = get(camera_cloud_storage).slotAssignments,
	activePlan = get(camera_cloud_storage).activePlan,
	totalSlots = getSlotCountsFromSettings(get(user_settings)).all,
	hasPremium = (get(user_data)?.premium ?? 0) > 0,
	cameraCount = getCameraDevices(get(devices) ?? {}).length,
}: {
	device: Device;
	deviceStatus?: DeviceStatusG2;
	hasKeyBackup: boolean;
	slotAssignments?: Record<number, string | null>;
	activePlan?: CameraCloudStorageActivePlan | null;
	totalSlots?: number;
	hasPremium?: boolean;
	cameraCount?: number;
}): CameraCloudSlotState => {
	const needsSetup =
		isCameraEncryptionSupported(device, deviceStatus) &&
		(!isCameraEncryptionConfigured(device, deviceStatus) || !hasKeyBackup);

	const occupiedIds = Object.values(slotAssignments ?? {}).filter((id): id is string => !!id);
	const isAssigned = occupiedIds.some((id) => cameraSlotIdEquals(id, device?.id ?? ''));
	const usedSlots = occupiedIds.length;
	const hasFreeSlots = usedSlots < totalSlots && totalSlots > 0;

	return {
		action: resolveCameraCloudSlotAction({
			needsSetup,
			isAssigned,
			isAssignable: isCameraAssignableToCloudSlot(device),
			hasFreeSlots,
			hasPremium,
			hasActivePlan: activePlan?.status === CameraCloudStorageSubscriptionStatus.Active,
			tier: activePlan?.tier ?? 0,
			cameraCount,
		}),
		needsSetup,
		isAssigned,
		hasFreeSlots,
		totalSlots,
		usedSlots,
		activePlan: activePlan ?? null,
	};
};

/**
 * Wording for the {@link getCameraCloudSlotState} CTA, so every camera surface
 * renders the same button. `null` for `none`, where no button is shown.
 */
export const getCameraCloudSlotActionLabel = (
	action: CameraCloudSlotAction,
): { key: string; default: string } | null => {
	switch (action) {
		case 'setup':
			return { key: 'setup', default: 'Setup' };
		case 'activate':
			return { key: 'button_activate', default: 'Activate' };
		case 'premium':
			return { key: 'button_promo_get_premium', default: 'Get Premium' };
		case 'manage':
			return { key: 'button_manage', default: 'Manage' };
		case 'subscribe':
			return {
				key: 'button_subscription_subscribe',
				default: 'Subscribe',
			};
		case 'upgrade':
			return { key: 'button_subscription_upgrade', default: 'Upgrade' };
		default:
			return null;
	}
};
